import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Collapse, Tag, Button, message, Spin, Segmented, Badge, Tooltip, Empty, Select, Space } from "antd";
import {
    ReloadOutlined,
    CheckCircleTwoTone,
    CheckOutlined,
    RobotOutlined,
    QuestionCircleOutlined,
    StopOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { getConfig } from "../../config";

const { API_BASE_URL } = getConfig();
const AUTH = "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU";

type ProposalStatus = "PENDING" | "CONFIRMED" | "ADJUSTED" | "SKIPPED" | "RESOLVED";

interface ProposalCard {
    supplement: string;
    protocol: string;
    description: string | null;
    cardStatus: string | null;
    aiJobCode: string | null;
    aiJobCodeDescription: string | null;
    aiConfidence: number | null;
    aiReasoning: string | null;
    chosenJobCode: string | null;
    status: ProposalStatus;
}
interface ProposalProject {
    projectNo: string;
    projectName: string | null;
    projectManager: string | null;
    pendingCount: number;
    cards: ProposalCard[];
}
interface ProposalOverview {
    counts: { pending: number; decided: number; noSuggestion: number; total: number };
    projects: ProposalProject[];
}
interface JobCodeOption {
    alias: string;
    description: string;
    descriptionSpez: string;
}

type FilterKey = "PENDING" | "NO_SUGGESTION" | "ALL";

const OrderCardProductAssignment: React.FC = () => {
    const [data, setData] = useState<ProposalOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState<FilterKey>("PENDING");
    const [pmFilter, setPmFilter] = useState<string | undefined>(undefined);
    // JobCode-Optionen je Projekt — lazy beim Aufklappen geladen und gecacht.
    const [jobCodesByProject, setJobCodesByProject] = useState<Record<string, JobCodeOption[]>>({});
    // Abweichende Select-Auswahl je Karte (Key: projectNo::supplement::protocol).
    const [selection, setSelection] = useState<Record<string, string>>({});
    const [savingKey, setSavingKey] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get<ProposalOverview>(`${API_BASE_URL}/getOrderCardProductProposals?auth=${AUTH}`);
            setData(res.data);
        } catch (e) {
            console.error("Fehler beim Laden der Vorschläge:", e);
            message.error("Vorschläge konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const refresh = async () => {
        setRefreshing(true);
        const hide = message.loading("KI-Vorschläge werden über alle Projekte generiert — das kann einige Minuten dauern …", 0);
        try {
            const res = await axios.get(`${API_BASE_URL}/refreshOrderCardProductProposals?auth=${AUTH}`, { timeout: 600000 });
            await load();
            message.success(
                `Fertig: ${res.data?.cardsWithoutProduct ?? "?"} Karten ohne Produkt, ${res.data?.aiSuggestions ?? "?"} KI-Vorschläge.`
            );
        } catch (e) {
            console.error("Fehler beim Aktualisieren:", e);
            message.error("Aktualisieren fehlgeschlagen.");
        } finally {
            hide();
            setRefreshing(false);
        }
    };

    const loadJobCodes = useCallback(async (projectNo: string) => {
        if (jobCodesByProject[projectNo]) return;
        try {
            const res = await axios.get<JobCodeOption[]>(
                `${API_BASE_URL}/getNetworkPlanJobCodes?auth=${AUTH}&projectNo=${projectNo}`
            );
            setJobCodesByProject((prev) => ({ ...prev, [projectNo]: res.data || [] }));
        } catch (e) {
            console.error(`JobCodes für ${projectNo} nicht ladbar:`, e);
        }
    }, [jobCodesByProject]);

    const cardKey = (p: ProposalProject, c: ProposalCard) => `${p.projectNo}::${c.supplement}::${c.protocol}`;

    const decide = async (project: ProposalProject, card: ProposalCard) => {
        const key = cardKey(project, card);
        const selected = selection[key] ?? card.aiJobCode ?? undefined;
        if (!selected) {
            message.warning("Bitte zuerst einen JobCode wählen.");
            return;
        }
        // KI-Vorschlag unverändert übernommen -> CONFIRM, sonst ADJUST.
        const action = selected === card.aiJobCode ? "CONFIRM" : "ADJUST";
        await sendDecision(project, card, action, selected);
    };

    const skip = async (project: ProposalProject, card: ProposalCard) => {
        await sendDecision(project, card, "SKIP", undefined);
    };

    const sendDecision = async (
        project: ProposalProject,
        card: ProposalCard,
        action: "CONFIRM" | "ADJUST" | "SKIP",
        jobCode?: string
    ) => {
        const key = cardKey(project, card);
        setSavingKey(key);
        try {
            const res = await axios.post(`${API_BASE_URL}/setOrderCardProductProposal?auth=${AUTH}`, {
                projectNo: project.projectNo,
                supplement: card.supplement,
                protocol: card.protocol,
                action,
                jobCode,
            });
            if (res.data?.success) {
                // Lokal aktualisieren statt komplett neu laden.
                setData((prev) => {
                    if (!prev) return prev;
                    const wasPending = card.status === "PENDING";
                    return {
                        ...prev,
                        counts: {
                            ...prev.counts,
                            pending: wasPending ? prev.counts.pending - 1 : prev.counts.pending,
                            decided: wasPending ? prev.counts.decided + 1 : prev.counts.decided,
                        },
                        projects: prev.projects.map((p) =>
                            p.projectNo !== project.projectNo
                                ? p
                                : {
                                      ...p,
                                      pendingCount: wasPending ? p.pendingCount - 1 : p.pendingCount,
                                      cards: p.cards.map((c) =>
                                          c.supplement === card.supplement && c.protocol === card.protocol
                                              ? { ...c, status: res.data.status, chosenJobCode: res.data.chosenJobCode ?? null }
                                              : c
                                      ),
                                  }
                        ),
                    };
                });
                message.success(action === "SKIP" ? "Karte übersprungen." : `Zuweisung gespeichert (${res.data.chosenJobCode}).`);
            } else {
                message.error(res.data?.message || "Speichern fehlgeschlagen.");
            }
        } catch (e) {
            console.error("Fehler beim Speichern der Entscheidung:", e);
            message.error("Speichern fehlgeschlagen.");
        } finally {
            setSavingKey(null);
        }
    };

    const projectManagers = useMemo(() => {
        const set = new Set<string>();
        (data?.projects ?? []).forEach((p) => p.projectManager && set.add(p.projectManager));
        return Array.from(set).sort((a, b) => a.localeCompare(b, "de"));
    }, [data]);

    const visibleProjects = useMemo(() => {
        if (!data) return [];
        const keep = (c: ProposalCard) => {
            if (filter === "ALL") return true;
            if (filter === "NO_SUGGESTION") return c.status === "PENDING" && !c.aiJobCode;
            return c.status === "PENDING";
        };
        return data.projects
            .filter((proj) => !pmFilter || proj.projectManager === pmFilter)
            .map((proj) => ({ ...proj, cards: proj.cards.filter(keep) }))
            .filter((proj) => proj.cards.length > 0);
    }, [data, filter, pmFilter]);

    const renderDecided = (card: ProposalCard) => {
        if (card.status === "SKIPPED") {
            return (
                <span style={{ color: "#8c8c8c" }}>
                    <StopOutlined /> übersprungen
                </span>
            );
        }
        if (card.status === "RESOLVED") {
            return (
                <span style={{ color: "#8c8c8c" }}>
                    <CheckOutlined /> in Abacus gelöst ({card.chosenJobCode})
                </span>
            );
        }
        return (
            <span>
                <CheckCircleTwoTone twoToneColor="#52c41a" />{" "}
                <span style={{ color: "#52c41a" }}>
                    {card.chosenJobCode} {card.status === "ADJUSTED" ? "(angepasst)" : "bestätigt"}
                </span>
            </span>
        );
    };

    const renderPending = (project: ProposalProject, card: ProposalCard) => {
        const key = cardKey(project, card);
        const options = (jobCodesByProject[project.projectNo] || []).map((jc) => ({
            value: jc.alias,
            label: `${jc.alias} — ${jc.description || jc.descriptionSpez || ""}`,
        }));
        const value = selection[key] ?? card.aiJobCode ?? undefined;
        return (
            <Space wrap>
                {card.aiJobCode ? (
                    <Tooltip title={card.aiReasoning || "KI-Vorschlag"}>
                        <Tag icon={<RobotOutlined />} color="blue">
                            {card.aiJobCode} · {card.aiConfidence ?? "?"}%
                        </Tag>
                    </Tooltip>
                ) : (
                    <Tooltip title={card.aiReasoning || "Die KI konnte keinen passenden JobCode finden."}>
                        <Tag icon={<QuestionCircleOutlined />} color="default">
                            kein Vorschlag
                        </Tag>
                    </Tooltip>
                )}
                <Select
                    size="small"
                    style={{ minWidth: 240 }}
                    placeholder="JobCode wählen"
                    value={value}
                    options={options}
                    showSearch
                    optionFilterProp="label"
                    onDropdownVisibleChange={(open) => open && loadJobCodes(project.projectNo)}
                    onChange={(v) => setSelection((prev) => ({ ...prev, [key]: v }))}
                />
                <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={savingKey === key}
                    style={{ background: "#153e8c" }}
                    onClick={() => decide(project, card)}
                >
                    Bestätigen
                </Button>
                <Tooltip title="Bewusst nicht zuordnen">
                    <Button size="small" icon={<StopOutlined />} onClick={() => skip(project, card)} />
                </Tooltip>
            </Space>
        );
    };

    const metric = (label: string, value: number, color: string) => (
        <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "10px 16px", minWidth: 96 }}>
            <div style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2, color }}>{value}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</div>
        </div>
    );

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
                <span style={{ flex: 1, color: "#595959" }}>
                    Auftragskarten ohne Produkt-Zuweisung - KI-Vorschläge prüfen und bestätigen. Bestätigte
                    Zuweisungen werden später gesammelt nach Abacus übertragen.
                </span>
                <Button icon={<ReloadOutlined />} loading={refreshing} onClick={refresh}>
                    KI-Vorschläge aktualisieren
                </Button>
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                {metric("offen", data?.counts.pending ?? 0, "#153e8c")}
                {metric("entschieden", data?.counts.decided ?? 0, "#52c41a")}
                {metric("ohne Vorschlag", data?.counts.noSuggestion ?? 0, "#d48806")}
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <Segmented
                    value={filter}
                    onChange={(v) => setFilter(v as FilterKey)}
                    options={[
                        { label: "Nur offen", value: "PENDING" },
                        { label: "Ohne Vorschlag", value: "NO_SUGGESTION" },
                        { label: "Alle", value: "ALL" },
                    ]}
                />
                <Select
                    allowClear
                    showSearch
                    placeholder="Projektleiter"
                    style={{ minWidth: 220 }}
                    value={pmFilter}
                    onChange={(v) => setPmFilter(v)}
                    options={projectManagers.map((pm) => ({ value: pm, label: pm }))}
                />
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: 48 }}>
                    <Spin size="large" />
                </div>
            ) : visibleProjects.length === 0 ? (
                <Empty description={'Keine Auftragskarten in dieser Ansicht — ggf. zuerst „KI-Vorschläge aktualisieren“ ausführen'} />
            ) : (
                <Collapse
                    defaultActiveKey={visibleProjects.slice(0, 1).map((p) => p.projectNo)}
                    onChange={(keys) => (Array.isArray(keys) ? keys : [keys]).forEach((k) => loadJobCodes(String(k)))}
                >
                    {visibleProjects.map((project) => (
                        <Collapse.Panel
                            key={project.projectNo}
                            header={
                                <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                    <strong>{project.projectNo}</strong>
                                    <span style={{ color: "#8c8c8c" }}>{project.projectName}</span>
                                    {project.projectManager && (
                                        <Tag color="blue">PL: {project.projectManager}</Tag>
                                    )}
                                    {project.pendingCount > 0 && (
                                        <Badge count={`${project.pendingCount} offen`} style={{ backgroundColor: "#153e8c" }} />
                                    )}
                                </span>
                            }
                        >
                            {project.cards.map((card) => (
                                <div
                                    key={`${card.supplement}-${card.protocol}`}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        padding: "10px 4px",
                                        borderBottom: "1px solid #f0f0f0",
                                        flexWrap: "wrap",
                                        opacity: card.status === "PENDING" ? 1 : 0.7,
                                    }}
                                >
                                    <Tag color="default" style={{ fontFamily: "monospace" }}>
                                        {card.supplement}-{card.protocol}
                                    </Tag>
                                    <span style={{ flex: 1, minWidth: 180 }}>{card.description}</span>
                                    {card.cardStatus && (
                                        <Tag color="default" style={{ color: "#8c8c8c" }}>
                                            {card.cardStatus}
                                        </Tag>
                                    )}
                                    <div style={{ minWidth: 320, display: "flex", justifyContent: "flex-end" }}>
                                        {card.status === "PENDING" ? renderPending(project, card) : renderDecided(card)}
                                    </div>
                                </div>
                            ))}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            )}
        </div>
    );
};

export default OrderCardProductAssignment;
